import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Badge, Card, Container, Row, Col } from 'react-bootstrap';
import { MapPin, Calendar, Check, ExternalLink, Edit, Eye, Navigation, ImagePlus } from 'lucide-react';
import './ProfilePage.scss';
import Navbar from '../Navbar/Navbar';
import Footer from '../Footer';
import MosqueLoader from '../../utils/MosqueLoader';
import { publicRequest } from '../../requestMethods';
import { getDishDisplayName } from '../../utils/dishDisplay';
import { DEFAULT_AVATAR } from '../../utils/constants';
import { useSnackbar } from '../../contexts/SnackbarContext';

const ProfilePage = () => {
    const { emailid } = useParams();
    const navigate = useNavigate();
    const snackbar = useSnackbar();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [contributorData, setContributorData] = useState(null);
    const [user] = useState(() => JSON.parse(localStorage.getItem('user')));
    const [showEditModal, setShowEditModal] = useState(false);
    const [editName, setEditName] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [editImageFile, setEditImageFile] = useState(null);
    const [editImagePreview, setEditImagePreview] = useState(null);
    const [editImageUrl, setEditImageUrl] = useState('');
    const [editSubmitting, setEditSubmitting] = useState(false);
    const [editError, setEditError] = useState(null);

    const decodedEmail = emailid ? decodeURIComponent(emailid) : '';

    useEffect(() => {
        const fetchContributorData = async () => {
            if (!decodedEmail) return;
            try {
                setLoading(true);
                const response = await publicRequest.get(`/mosques/contributor/${encodeURIComponent(decodedEmail)}`);
                setContributorData(response.data);
                setError(null);
            } catch (err) {
                setError(err.response?.data?.message || 'Error fetching contributor data');
                setContributorData(null);
            } finally {
                setLoading(false);
            }
        };

        fetchContributorData();
    }, [decodedEmail]);

    useEffect(() => {
        if (showEditModal) {
            document.body.style.overflow = 'hidden';
            setEditName(contributorData?.name || '');
            setEditPhone('');
            setEditImageFile(null);
            setEditImagePreview(null);
            setEditImageUrl('');
            setEditError(null);
            if (contributorData?._id && user?.email === contributorData._id) {
                publicRequest.get(`/users/profile?email=${encodeURIComponent(contributorData._id)}`)
                    .then((res) => {
                        if (res.data?.profile?.phone) setEditPhone(res.data.profile.phone);
                    })
                    .catch(() => {});
            }
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [showEditModal, contributorData?._id, user?.email]);

    const handleEditImageChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            setEditImageFile(file);
            setEditImagePreview(URL.createObjectURL(file));
        } else {
            setEditImageFile(null);
            setEditImagePreview(null);
        }
    };

    const handleEditSubmit = async (e) => {
        e?.preventDefault();
        if (!user || !contributorData || editSubmitting) return;
        setEditSubmitting(true);
        setEditError(null);
        try {
            let pictureUrl = contributorData.picture;
            const imageUrlTrimmed = editImageUrl?.trim();
            if (editImageFile) {
                try {
                    const formData = new FormData();
                    formData.append('image', editImageFile);
                    formData.append('user', JSON.stringify({
                        name: user.name,
                        email: user.email,
                        picture: user.picture,
                        googleId: user.googleId || user.sub
                    }));
                    const uploadRes = await publicRequest.post('/users/upload-profile-picture', formData);
                    pictureUrl = uploadRes.data.imageUrl;
                } catch (uploadErr) {
                    if (imageUrlTrimmed) {
                        pictureUrl = imageUrlTrimmed;
                        snackbar.info('Upload failed; using image URL instead.');
                    } else {
                        throw uploadErr;
                    }
                }
            } else if (imageUrlTrimmed) {
                pictureUrl = imageUrlTrimmed;
            }
            await publicRequest.patch('/users/profile', {
                email: user.email,
                user: { email: user.email, googleId: user.googleId || user.sub },
                name: editName.trim() || undefined,
                picture: pictureUrl,
                phone: editPhone.trim() || undefined
            });
            const response = await publicRequest.get(`/mosques/contributor/${encodeURIComponent(decodedEmail)}`);
            setContributorData(response.data);
            setShowEditModal(false);
            snackbar.success('Profile updated successfully');
        } catch (err) {
            setEditError(err.response?.data?.message || 'Failed to update profile');
            snackbar.error(err.response?.data?.message || 'Failed to update profile');
        } finally {
            setEditSubmitting(false);
        }
    };

    const isOwner = user && contributorData && (
        user.email === contributorData._id ||
        (user.googleId || user.sub) === contributorData.googleId
    );

    const MosqueCard = ({ mosque }) => (
        <Col md={6} lg={4} className="mb-4">
            <Card
                className="mosque-card"
                onClick={() => navigate(`/mosque/${mosque.mosqueId}`)}
            >
                <Card.Body>
                    <div className="mosque-card__header">
                        <h3>{mosque.name}</h3>
                        {mosque.verified && (
                            <Badge bg="success" className="d-flex align-items-center gap-1">
                                <Check size={14} /> Verified
                            </Badge>
                        )}
                    </div>

                    <div className="mosque-card__info">
                        <p className="d-flex align-items-center gap-2">
                            <MapPin size={16} />
                            {mosque.district}
                        </p>
                        <p className="d-flex align-items-center gap-2">
                            <Calendar size={16} />
                            {new Date(mosque.date).toLocaleDateString()}
                        </p>
                        {(mosque.visits != null || mosque.directionClicks != null) && (
                            <div className="mosque-card__stats">
                                {mosque.visits != null && (
                                    <span className="mosque-card__stat">
                                        <Eye size={14} />
                                        {mosque.visits} views
                                    </span>
                                )}
                                {mosque.directionClicks != null && (
                                    <span className="mosque-card__stat">
                                        <Navigation size={14} />
                                        {mosque.directionClicks} directions
                                    </span>
                                )}
                            </div>
                        )}
                        {mosque.dish && (
                            <p className="mosque-card__dish">
                                {getDishDisplayName(mosque.dish)}
                            </p>
                        )}
                    </div>

                    {mosque.googleMapsLink && (
                        <a
                            href={mosque.googleMapsLink}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="mosque-card__maps-link"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <ExternalLink size={14} />
                            View on Maps
                        </a>
                    )}
                </Card.Body>
            </Card>
        </Col>
    );

    const StatItem = ({ label, value, emoji }) => (
        <div className="profile-page__stat-item">
            <span className="profile-page__stat-emoji">{emoji}</span>
            <div className="profile-page__stat-content">
                <span className="profile-page__stat-value">{value}</span>
                <span className="profile-page__stat-label">{label}</span>
            </div>
        </div>
    );

    return (
        <>
            <Navbar />
            {loading && (
                <MosqueLoader />
            )}
            {error && !contributorData && (
                <div className="profile-page__error">
                    <h3>Error</h3>
                    <p>{error}</p>
                </div>
            )}
            {contributorData && (
                <Container className="profile-page">
                    {/* Profile Header */}
                    <div className="profile-page__header">
                        {isOwner && (
                            <button
                                className="profile-page__edit-btn"
                                onClick={() => setShowEditModal(true)}
                                title="Edit profile"
                            >
                                <Edit size={18} />
                                Edit Profile
                            </button>
                        )}
                        <div className="profile-page__avatar">
                            <img
                                src={contributorData.picture || DEFAULT_AVATAR}
                                alt={contributorData.name}
                                onError={(e) => {
                                    e.target.src = DEFAULT_AVATAR;
                                    e.target.onerror = null;
                                }}
                            />
                        </div>
                        <h1>{contributorData.name}</h1>
                    </div>

                    {/* Stats Section */}
                    <div className="profile-page__stats">
                        <StatItem
                            emoji="🕌"
                            label="Total Mosques"
                            value={contributorData.totalMosques}
                        />
                        <StatItem
                            emoji="👁️"
                            label="Total Views"
                            value={contributorData.totalVisits ?? 0}
                        />
                        <StatItem
                            emoji="📤"
                            label="Directions Clicked"
                            value={contributorData.totalDirectionClicks ?? 0}
                        />
                    </div>

                    {/* Mosques List */}
                <div className="profile-page__mosques">
                    <h2 className="text-center mb-4">Contributed Mosques</h2>
                    <Row>
                        {contributorData.mosques
                            .sort((a, b) => new Date(b.date) - new Date(a.date))
                            .map((mosque) => (
                                <MosqueCard key={mosque.mosqueId} mosque={mosque} />
                            ))}
                    </Row>

                    {contributorData.mosques.length === 0 && (
                        <div className="text-center py-5">
                            <p className="text-muted">No mosques contributed yet.</p>
                        </div>
                    )}
                </div>
                </Container>
            )}

            {showEditModal && isOwner && (
                <div
                    className="profile-edit-overlay"
                    onClick={() => !editSubmitting && setShowEditModal(false)}
                >
                    <div className="profile-edit-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="profile-edit-modal__header">
                            <h3>Edit Profile</h3>
                            <button
                                type="button"
                                className="profile-edit-modal__close"
                                onClick={() => !editSubmitting && setShowEditModal(false)}
                            >
                                ×
                            </button>
                        </div>
                        <form className="profile-edit-form" onSubmit={handleEditSubmit}>
                            <label>
                                Name
                                <input
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    placeholder="Your name"
                                />
                            </label>
                            <label>
                                Profile Photo
                                <div className="profile-edit-image-area">
                                    {editImagePreview ? (
                                        <div className="profile-edit-image-preview">
                                            <img src={editImagePreview} alt="Preview" />
                                            <button
                                                type="button"
                                                className="profile-edit-remove-image"
                                                onClick={() => {
                                                    setEditImageFile(null);
                                                    setEditImagePreview(null);
                                                }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    ) : (
                                        <label className="profile-edit-image-label">
                                            <ImagePlus size={32} />
                                            <span>Click to upload new photo</span>
                                            <input
                                                type="file"
                                                accept="image/*"
                                                onChange={handleEditImageChange}
                                                hidden
                                            />
                                        </label>
                                    )}
                                    <p className="profile-edit-image-or">or paste image URL</p>
                                    <input
                                        type="url"
                                        value={editImageUrl}
                                        onChange={(e) => setEditImageUrl(e.target.value)}
                                        placeholder="https://example.com/your-photo.jpg"
                                        className="profile-edit-image-url"
                                    />
                                </div>
                            </label>
                            <label>
                                Phone (optional)
                                <input
                                    type="tel"
                                    value={editPhone}
                                    onChange={(e) => setEditPhone(e.target.value)}
                                    placeholder="Your phone number"
                                />
                            </label>
                            {editError && (
                                <div className="profile-edit-error">{editError}</div>
                            )}
                            <div className="profile-edit-actions">
                                <button
                                    type="button"
                                    className="profile-edit-cancel"
                                    onClick={() => setShowEditModal(false)}
                                    disabled={editSubmitting}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="profile-edit-submit"
                                    disabled={editSubmitting || !editName.trim()}
                                >
                                    {editSubmitting ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <Footer />

        </>
    );
};

export default ProfilePage;